/** Backlog #25 AI summaries and #27 translation. */

export type AiMode = 'llm' | 'basic';

export interface AiStatus {
  llm: boolean; model: string | null; transcription: boolean; recording: boolean;
  translation: { provider: TranslationProvider | null; languages: { code: string; name: string }[] };
  runs_per_hour: number; used_this_hour: number;
}

// ------------------------------------------------------------------------------------------------ catch me up
export interface CatchUpRequest { conversation_id: string; parent_id?: string | null; since?: string | null }
export interface CatchUpHighlight { message_id: string; sequence: number; author_name: string; text: string; reason: string }
export interface CatchUpActionItem { text: string; owner_name: string | null; message_id: string | null }
export interface CatchUp {
  mode: AiMode; notice: string | null; conversation_id: string; parent_id: string | null;
  since: string; until: string; message_count: number; people: { name: string; count: number }[];
  summary: string; highlights: CatchUpHighlight[]; action_items: CatchUpActionItem[]; mentions_me: number; open_questions: CatchUpHighlight[];
  /** t530u: whether an AI model is set up for this workspace (LlmService.configuredFor), whatever mode this result is in. */
  ai_configured?: boolean;
}

/**
 * t530u: the small note under a catch-up (web and phone). Nothing when there was nothing to summarise; the AI caveat for
 * an AI summary; "not set up" only when no model is set up. A model that couldn't be reached says so in `notice` instead.
 */
export function catchUpFooter(d: Pick<CatchUp, 'mode' | 'message_count' | 'ai_configured'> | null | undefined, model?: string | null): string | null {
  if (!d || d.message_count === 0) return null;
  if (d.mode === 'llm') return `AI summary${model ? ` (${model})` : ''} – check anything important against the messages.`;
  if (d.ai_configured === false) return 'Quick overview – AI summaries aren’t set up for this workspace.';
  return null;
}

// ------------------------------------------------------------------------------------------------ weekly digests
export interface DigestConfig {
  id: string; location_id: string | null; team_id: string | null; scope_name: string; conversation_id: string | null; conversation_name: string | null;
  weekday: number; hour: number; enabled: boolean; last_digest_at: string | null; can_manage: boolean;
}
export interface DigestConfigInput { location_id?: string | null; team_id?: string | null; conversation_id?: string | null; weekday?: number; hour?: number; enabled?: boolean }
export interface DigestStats {
  people: number;
  announcements: { id: string; title: string; priority: string }[];
  incidents: { opened: number; resolved: number; open_now: number; overdue: number; top: { id: string; title: string; severity: string }[] };
  checklists: { due: number; completed: number; missed: number; avg_audit_score: number | null };
  handovers: number;
  tasks_completed: number;
  kudos: number;
  leave_next_week: { name: string; from: string; to: string }[];
  birthdays_next_week: { name: string; date: string }[];
  new_starters: { name: string; start_date: string }[];
}
export interface Digest {
  id: string; config_id: string; scope_name: string; period_start: string; period_end: string; title: string; summary: string;
  stats: DigestStats; mode: AiMode; created_at: string;
  /** t273u: whether the digest is for a building or a team (scope_name is its current name). */
  scope_kind?: 'building' | 'team';
  /** t277u: you haven't opened it yet (or marked it unread). */
  unread?: boolean;
}

// ------------------------------------------------------------------------------------------------ meeting notes
export type RecordingStatus = 'awaiting_consent' | 'recording' | 'processing' | 'done' | 'failed' | 'cancelled';
export interface MeetingActionItem { text: string; owner_name: string | null; owner_id: string | null; due: string | null; task_id: string | null }
export interface CallRecording {
  id: string; call_id: string; conversation_id: string | null; requested_by: string; requested_by_name: string; status: RecordingStatus;
  consents: { user_id: string; display_name: string; decision: 'granted' | 'declined' | null }[];
  started_at: string | null; stopped_at: string | null; processed_at: string | null; duration_seconds: number | null;
  summary: string | null; action_items: MeetingActionItem[]; transcript_available: boolean; transcript_delete_after: string | null;
  mode: AiMode | null; error: string | null; my_decision: 'granted' | 'declined' | null;
}

// ------------------------------------------------------------------------------------------------ translation
export type TranslationProvider = 'llm' | 'deepl' | 'libretranslate';
export type TranslatableType = 'message' | 'task_comment' | 'announcement' | 'article';
export interface TranslateItem { type: TranslatableType; id: string }
export interface Translated {
  type: TranslatableType; id: string; target_lang: string; source_lang: string | null; same_language: boolean; provider: TranslationProvider;
  /** Plain text items (messages, comments). */
  text?: string;
  /** Rich items (announcements, articles): translated title and sanitised HTML. */
  title?: string; html?: string;
}

/** Languages offered in the picker. Codes are ISO 639-1 (DeepL-style region only for en/pt). */
export const LANGUAGES: { code: string; name: string; native: string }[] = [
  { code: 'en', name: 'English', native: 'English' }, { code: 'pl', name: 'Polish', native: 'Polski' },
  { code: 'ro', name: 'Romanian', native: 'Română' }, { code: 'lt', name: 'Lithuanian', native: 'Lietuvių' },
  { code: 'lv', name: 'Latvian', native: 'Latviešu' }, { code: 'bg', name: 'Bulgarian', native: 'Български' },
  { code: 'uk', name: 'Ukrainian', native: 'Українська' }, { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'es', name: 'Spanish', native: 'Español' }, { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'fr', name: 'French', native: 'Français' }, { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'hu', name: 'Hungarian', native: 'Magyar' }, { code: 'sk', name: 'Slovak', native: 'Slovenčina' },
  { code: 'cs', name: 'Czech', native: 'Čeština' }, { code: 'cy', name: 'Welsh', native: 'Cymraeg' },
  { code: 'ur', name: 'Urdu', native: 'اردو' }, { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'ar', name: 'Arabic', native: 'العربية' }, { code: 'zh', name: 'Chinese', native: '中文' },
];
export const languageName = (code: string | null | undefined) => (code ? LANGUAGES.find(l => l.code === code.slice(0, 2))?.name ?? code : 'Unknown');

/**
 * Split text into translatable segments and protected tokens (mentions, URLs, code spans, emoji shortcodes) so
 * providers never rewrite them. `join` puts the translated segments back around the untouched tokens.
 */
const PROTECT = /(`[^`]+`|\b[A-Z][A-Z0-9]{1,9}-\d{1,7}\b|https?:\/\/\S+|<@[0-9a-f-]{36}>|@[\w.-]+|:[a-z0-9_+-]+:)/g;
export function protectTokens(text: string): { template: string; tokens: string[] } {
  const tokens: string[] = [];
  const template = text.replace(PROTECT, m => { tokens.push(m); return `⟦${tokens.length - 1}⟧`; });
  return { template, tokens };
}
export function restoreTokens(template: string, tokens: string[]): string {
  return template.replace(/⟦(\d+)⟧/g, (m, i: string) => tokens[Number(i)] ?? m);
}

/** Monday (ISO) of the week containing `d`, as YYYY-MM-DD (UTC date maths). */
export function digestWeekStart(d: string): string {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
}

/** Parse the model's JSON reply, tolerating ```json fences and leading prose. */
export function parseModelJson<T>(raw: string): T | null {
  const s = raw.replace(/^[\s\S]*?```(?:json)?\s*/i, m => (m.includes('```') ? '' : m)).replace(/```[\s\S]*$/, '');
  const start = s.search(/[[{]/);
  if (start < 0) return null;
  const end = Math.max(s.lastIndexOf('}'), s.lastIndexOf(']'));
  try { return JSON.parse(s.slice(start, end + 1)) as T; } catch { return null; }
}

/** t445u: Settings > Integrations > Call recordings. `enabled` is the same switch as Meeting notes under AI & translation. */
export interface CallRecordingSettingsView {
  enabled: boolean;
  /** 0 = the audio is deleted as soon as the notes are made. */
  audio_retention_days: number;
  transcript_retention_days: number;
  /** Recording can start in this workspace (switched on, recorder running, speech-to-text available). */
  ready: boolean;
  /** Who turns speech into text and writes the notes: the workspace's Gemini key, the server's model, or nobody yet. */
  engine: { kind: 'gemini' | 'server'; model: string } | null;
  transcription: boolean;
  recorder: 'ready' | 'no_calls' | 'no_recorder' | 'no_storage';
  storage: 'app' | 'custom' | null;
  updated_at: string | null;
  updated_by_name: string | null;
}
export interface CallRecordingSettingsInput { enabled?: boolean; audio_retention_days?: number; transcript_retention_days?: number }

/** t445u: keys for other services, saved per workspace like the Gemini key (sealed, write-only, shown by last 4). */
export type IntegrationKeyName = 'google_maps' | 'google_maps_browser' | 'dvla';
export interface IntegrationKeyView {
  name: IntegrationKeyName;
  /** Where the key in use comes from: saved here, set on the server, or none. */
  source: 'settings' | 'server' | 'none';
  last4: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
}
export interface IntegrationKeysView { keys: IntegrationKeyView[]; can_store_secrets: boolean }
export interface IntegrationKeysInput { set?: Partial<Record<IntegrationKeyName, string>>; clear?: IntegrationKeyName[] }
export interface IntegrationKeyTest { ok: boolean; detail: string }
